/**
 * Ícones do menu lateral (JOR4-7365, RN05).
 *
 * A biblioteca e o conjunto vêm do Design System (foundations/menu-icons.html):
 * Lucide, grupos em 16px e itens em 13px. Onde o DS nomeia o ícone de uma tela,
 * é esse que está aqui. As telas que o DS não cobre — o protótipo desenhou um
 * recorte do menu, não o menu inteiro — recebem o ícone mais próximo **dentro da
 * mesma paleta do DS**, nunca um ícone de fora, para o conjunto continuar
 * coerente.
 *
 * O vínculo é resolvido em cascata:
 *   1. `MNU_ICONE` — o nome cadastrado em `jmu_menu.jmu_icone`, traduzido para o
 *      ícone do DS. É o que permite uma tela nova ganhar ícone sem deploy;
 *   2. TLA_CLASSEGRID — o mapa que veio do protótipo, e a rede de segurança para
 *      nome cadastrado que ainda não tenha tradução;
 *   3. título normalizado — para os grupos, que não têm classe;
 *   4. fallback — obrigatório: uma tela nova não pode quebrar a renderização do
 *      menu só porque ninguém lembrou de definir o ícone.
 */

import {
    Activity,
    AlertCircle,
    AlertTriangle,
    Award,
    Ban,
    BarChart2,
    Bed,
    Bell,
    Bot,
    Building,
    Building2,
    Calendar,
    CalendarDays,
    CheckCheck,
    Circle,
    ClipboardCheck,
    ClipboardList,
    Clock,
    FileOutput,
    FileSignature,
    FileText,
    Flag,
    Folder,
    GitBranch,
    GitFork,
    History,
    Home,
    KeyRound,
    Layers,
    LayoutDashboard,
    Mail,
    Megaphone,
    Menu,
    MessageSquare,
    Milestone,
    Monitor,
    Moon,
    Network,
    Package,
    Palmtree,
    PenSquare,
    PieChart,
    ScrollText,
    Settings,
    Settings2,
    Shield,
    Smartphone,
    Sparkles,
    StickyNote,
    Timer,
    TrendingUp,
    Truck,
    UserCheck,
    UserCog,
    Users,
    Utensils,
    Wallet,
    Warehouse,
    Wrench,
    Zap,
    type LucideIcon,
} from "lucide-react";

import { normalizeText, type ItemMenu } from "./montarMenu";

/*
 * A versão do lucide-react está cravada em 0.263.1, não é descuido: as versões
 * novas publicam o build ESM com `??` sem transpilar, e o parser do webpack 4
 * não lê. A 0.263.1 é a última que compila aqui, e tem todos os ícones que o DS
 * pede. Junto com ela, webpack.config.js e webpack.prod.js ganharam a regra que
 * trata .mjs como "javascript/auto" — sem ela o webpack 4 recusa os imports que
 * o pacote faz do React.
 *
 * Os nomes usados aqui (PenSquare, BarChart2, AlertTriangle, Palmtree) são os
 * do DS. No Lucide atual viraram apelidos de SquarePen, ChartNoAxesColumn,
 * TriangleAlert e TreePalm; mantê-los deixa o código conferível linha a linha
 * contra o DS, e se um apelido sumir num upgrade o build quebra no import — que
 * é o comportamento desejado, e não um ícone errado em silêncio.
 */

/** Tamanhos do DS. */
export const TAMANHO_ICONE_GRUPO = 16;
export const TAMANHO_ICONE_ITEM = 13;

/**
 * Fallbacks. Não fazem parte da paleta do DS de propósito: são neutros, para
 * ficar evidente na tela que aquele item ainda não tem ícone definido.
 */
const ICONE_PADRAO_GRUPO = Folder;
const ICONE_PADRAO_ITEM = Circle;

/** Ícone por TLA_CLASSEGRID. Comentário `DS` = ícone nomeado no Design System. */
const iconesPorClasse: Record<string, LucideIcon> = {
    // --- Cadastros ---
    TelaCadastroBIJornada: BarChart2, // DS: Painéis BI
    GridVeiculosJornada: Truck, // DS: Veículos Jornada
    GridEmpresasJornada: Building, // DS: Empresas Jornada
    GridMotoristasJornada: Users, // DS: Motoristas Jornada
    GridMotoristasTecnologia: Users, // DS: Motoristas tecnologia
    CadastroArvoreDecisao: GitFork, // DS: Config. árvore de decisões
    GridServicosJornada: Package, // DS: Serviços Jornada
    NovidadesSistema: Megaphone, // DS: Novidades
    CadastroEmbarcadores: Building2,
    SystemIndicatorsManagement: TrendingUp,
    IndicatorSectionsManagement: ClipboardList,

    // --- Jornada ---
    GridControleJornada: Clock, // DS: Controle de Jornada
    GridControleJornada2: Clock,
    GridControleJornadaGo: Clock,
    TelaPaineisBIJornada: BarChart2, // DS: Painéis BI
    Dashboards: LayoutDashboard,
    AssinaturaEletronica: PenSquare,
    JornadaAssistida: Activity,
    /*
     * Manutenção da Jornada tem `fas fa-user-clock` cadastrado, que não está
     * traduzido — sem esta linha ela cai no fallback e aparece como um círculo
     * vazio, no menu e no acesso rápido. A chave inglesa é o desenho do
     * protótipo para essa tela; o PenSquare do grupo "Manutenção" já é o da
     * Assinatura Eletrônica, e dois ícones iguais confundiriam as duas.
     */
    ManutencaoJornada: Wrench,

    // --- Relatórios ---
    RelatorioProdutividade: PieChart, // DS: Relatório de Produtividade
    RelatorioEventos: History, // DS: Relatório de Eventos
    RelatorioExcecoes: Flag, // DS: Relatório de Exceções
    RelatorioMensagensJornada: MessageSquare, // DS: Relatório de mensagens
    RelatorioTratativaExcecoes: CheckCheck,
    RelatorioNaoConformidades: AlertTriangle,
    RelatorioAnomalias: Zap,
    RelatorioAfastamentos: Palmtree,
    RelatorioDiarias: Wallet,
    RelatorioBancoDeHoras: Timer,
    CartaoPontoSintetico: CalendarDays,
    CartaoPontoSinteticoGo: CalendarDays,
    CartaoPontoSinteticoAntigo: CalendarDays,
    ComprovantesRegistroPonto: FileOutput,
    RelatorioAcessos: KeyRound,
    RelatorioAnotacoesJornada: StickyNote,
    RelatorioKmDiariosMotoristas: Milestone,
    RelatorioAtivacaoMotoristas: UserCheck,
    RelatorioDSR: Bed,
    RelatorioFaltaDescansoRepouso: Moon,
    RelatorioHorasFalta: Utensils,
    RelatorioOcorrencias: AlertCircle,
    RelatorioAlteracoesViaMobile: Smartphone,
    RelatorioLogsAplicativo: ScrollText,
    RelatorioAlteracoesManuaisEventos: FileSignature,

    // --- Configurações ---
    ConfiguracoesMensagensAutomaticas: Mail,
    HistoricoConfiguracoesJornada: History, // JOR4-7718
    ChatAssistenteIA: Bot,
    MenusSistema: Menu,
    TelasSistema: Monitor,
    UsuariosSistema: UserCog,
    PermissoesSistema: Shield,
    PermissoesSistemaPorUsuario: Shield,
    PlanosSistema: Layers,
    VinculosEmpresaPlano: GitBranch,
    VinculosEmbarcadoraPlano: GitBranch,
    GruposEmpresas: Network,
    ConfiguracoesNotificacoes: Bell,
    Escalas: Calendar, // DS: Escalas
    Excecoes: Ban, // DS: Exceções

    // --- Jornada de Embarcadores ---
    ControleJornadaTerceirosTab: Clock,
    ControlePontuacaoPenalidadeTab: Award,
    RelatorioJornadaTerceiros: FileText,
    RelatorioNaoConformidadesTerceiros: AlertTriangle,
};

/**
 * Tradução do nome cadastrado em `jmu_menu.jmu_icone` para o ícone do Design
 * System.
 *
 * O banco guarda NOME no padrão Font Awesome — convenção que já existia lá
 * (`jmu_codigo` 94 = "fas fa-clipboard-check") —, e não markup. Guardar SVG
 * exigiria injetá-lo por innerHTML, e SVG aceita `<script>`, `onload` e
 * `<foreignObject>`: seria XSS armazenado, gravável por quem tem acesso à tela de
 * Menus.
 *
 * A tradução também é o que permite o cadastro mandar no ícone SEM quebrar duas
 * coisas: o conjunto continua sendo o do protótipo (Font Awesome é preenchido,
 * Lucide é traço de 2px — misturar apareceria), e o mapa estático preserva o
 * tree-shaking. Resolver o nome direto contra a biblioteca levaria o bundle de
 * 15 KB para 515 KB.
 *
 * Nome novo, ainda sem tradução aqui, cai na cascata seguinte (classe → título →
 * fallback), então nunca fica sem ícone.
 */
const iconesPorNomeCadastrado: Record<string, LucideIcon> = {
    "fas fa-award": Award,
    "fas fa-ban": Ban,
    "fas fa-bars": Menu,
    "fas fa-bed": Bed,
    "fas fa-bell": Bell,
    "fas fa-bolt": Zap,
    "fas fa-box": Package,
    "fas fa-building": Building,
    "fas fa-bullhorn": Megaphone,
    "fas fa-calendar": Calendar,
    "fas fa-calendar-alt": CalendarDays,
    "fas fa-chart-bar": BarChart2,
    "fas fa-chart-line": TrendingUp,
    "fas fa-chart-pie": PieChart,
    "fas fa-check-double": CheckCheck,
    "fas fa-city": Building2,
    "fas fa-clipboard-check": ClipboardCheck,
    "fas fa-clipboard-list": ClipboardList,
    "fas fa-clock": Clock,
    "fas fa-code-branch": GitBranch,
    "fas fa-cog": Settings,
    "fas fa-comment-alt": MessageSquare,
    "fas fa-desktop": Monitor,
    "fas fa-envelope": Mail,
    "fas fa-exclamation-circle": AlertCircle,
    "fas fa-exclamation-triangle": AlertTriangle,
    "fas fa-file-alt": FileText,
    "fas fa-file-export": FileOutput,
    "fas fa-file-signature": FileSignature,
    "fas fa-flag": Flag,
    "fas fa-heartbeat": Activity,
    "fas fa-history": History,
    "fas fa-home": Home,
    "fas fa-key": KeyRound,
    "fas fa-layer-group": Layers,
    "fas fa-magic": Sparkles,
    "fas fa-mobile-alt": Smartphone,
    "fas fa-moon": Moon,
    "fas fa-pen-square": PenSquare,
    "fas fa-project-diagram": GitFork,
    "fas fa-robot": Bot,
    "fas fa-route": Milestone,
    "fas fa-scroll": ScrollText,
    "fas fa-shield-alt": Shield,
    "fas fa-sitemap": Network,
    "fas fa-sliders-h": Settings2,
    "fas fa-sticky-note": StickyNote,
    "fas fa-stopwatch": Timer,
    "fas fa-tachometer-alt": LayoutDashboard,
    "fas fa-truck": Truck,
    "fas fa-umbrella-beach": Palmtree,
    "fas fa-user-check": UserCheck,
    "fas fa-user-cog": UserCog,
    "fas fa-users": Users,
    "fas fa-utensils": Utensils,
    "fas fa-wallet": Wallet,
    "fas fa-warehouse": Warehouse,
};

/**
 * Ícone por título, para os grupos — que vêm de `menus-allowed` sem
 * TLA_CLASSEGRID. As chaves cobrem tanto os títulos de produção ("Cadastros")
 * quanto os do protótipo ("Cadastros Jornada"), porque o rótulo do grupo é
 * configurável e as duas formas circulam.
 */
const iconesPorTitulo: Record<string, LucideIcon> = {
    inicio: Home, // DS: Início
    cadastros: Settings, // DS: Cadastros Jornada
    "cadastros jornada": Settings,
    "cadastros embarcador": Package,
    relatorios: FileText, // DS: Relatórios da Jornada
    "relatorios da jornada": FileText,
    "relatorios jornada de embarcadores": FileText,
    configuracoes: Shield, // DS: Configurações Jornada
    "configuracoes jornada": Shield,
    jornada: Activity, // DS: Jornada
    "jornada de embarcadores": Warehouse,
    manutencao: PenSquare, // DS: Manutenção
};

/**
 * Resolve o ícone de um item do menu, em cascata:
 *
 *   1. o nome cadastrado em `jmu_menu.jmu_icone` — quem cadastra manda, e sem
 *      deploy;
 *   2. TLA_CLASSEGRID — o mapa que vinha do protótipo;
 *   3. título normalizado, para os grupos, que não têm classe;
 *   4. fallback neutro.
 *
 * A ordem importa: um nome cadastrado que ainda não esteja traduzido cai no passo
 * 2 e a tela mantém o ícone que já tinha, em vez de regredir para o fallback.
 */
export const iconeDoMenu = (item: ItemMenu): LucideIcon => {
    if (item.icone) {
        const porCadastro = iconesPorNomeCadastrado[item.icone.trim()];
        if (porCadastro) return porCadastro;
    }

    if (item.classInvoke) {
        const porClasse = iconesPorClasse[item.classInvoke];
        if (porClasse) return porClasse;
    }

    const porTitulo = iconesPorTitulo[normalizeText(item.text || "")];
    if (porTitulo) return porTitulo;

    return item.classInvoke ? ICONE_PADRAO_ITEM : ICONE_PADRAO_GRUPO;
};
